"use client";

import { useMemo, useEffect } from "react";
import { type Variant, getCurrentVariant, VARIANTS } from "@/config/variants";
import { useDealership } from "@/contexts/DealershipContext";
import { getStoredLocale } from "@/utils/localeCookie";
import { i1 as i18n } from "@/i18n/i18n";

/**
 * React hook to get the current whitelabel variant.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const variant = useVariant();
 *
 *   return (
 *     <div>
 *       <h1>Current variant: {variant}</h1>
 *       {variant === VARIANTS.CADA && <CadaSpecificFeature />}
 *     </div>
 *   );
 * }
 * ```
 *
 * @returns The current variant ("nada" or "cada")
 */
export function useVariant(): Variant {
    const variant = useMemo(() => {
        return getCurrentVariant();
    }, []);

    // Handle default locale for CADA variant based on hostname detection
    useEffect(() => {
        // Only run on client-side
        if (typeof window === "undefined") {
            return;
        }

        const storedLocale = getStoredLocale();

        // If no locale is stored in cookie, set default based on variant
        if (!storedLocale) {
            const defaultLocale = variant === VARIANTS.CADA ? "fr-CA" : "en";

            // Change language if it's different from current
            if (i18n.language !== defaultLocale) {
                i18n.changeLanguage(defaultLocale);
            }
        }
    }, [variant]);

    return variant;
}

/**
 * React hook to check if the current variant matches a specific variant.
 * Useful for conditional rendering based on variant.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const isCada = useIsVariant(VARIANTS.CADA);
 *
 *   return (
 *     <div>
 *       {isCada ? (
 *         <CadaLogo />
 *       ) : (
 *         <NadaLogo />
 *       )}
 *     </div>
 *   );
 * }
 * ```
 *
 * @param targetVariant - The variant to check against
 * @returns True if the current variant matches the target variant
 */
export function useIsVariant(targetVariant: Variant): boolean {
    const currentVariant = useVariant();
    const { participantInfo } = useDealership();
    const isAtd = participantInfo?.car_truck === "T" && targetVariant !== "cada";

    return useMemo(() => {
        return isAtd || currentVariant === targetVariant;
    }, [currentVariant, isAtd, targetVariant]);
}

/**
 * React hook to check if the current variant is NOT the default NADA variant.
 * Useful when you need to apply whitelabel-specific behavior.
 *
 * @example
 * ```tsx
 * function Header() {
 *   const isWhitelabel = useIsWhitelabel();
 *
 *   return (
 *     <header>
 *       {isWhitelabel && <WhitelabelBanner />}
 *       <Navigation />
 *     </header>
 *   );
 * }
 * ```
 *
 * @returns True if the current variant is NOT "nada"
 */
export function useIsWhitelabel(): boolean {
    const currentVariant = useVariant();

    return useMemo(() => {
        return currentVariant !== VARIANTS.NADA;
    }, [currentVariant]);
}
